import { Document } from '@creditornot/cb-icons';
import { cbSemanticCssVars, colors, typographyCss } from '@creditornot/cb-ingredients';
import { cssThemedValue } from '@creditornot/cb-sc-utils';
import React from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { MOBILE_BREAKPOINT } from 'common/hooks/useIsMobile';
import { Body1, Body3 } from 'common/typography/Typography';

import appPreview from './app-preview.png';
import { BASE_STEPS_PREVIEW_INTL_ID } from './constants';
import PhoneActionSettings from './icons/PhoneActionSettings';
import Sparkle from './icons/Sparkle';

const CardContainer = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 24px 10px 0 24px;
  background: ${cssThemedValue({ light: 'var(--color-bg-surface-brand-disabled, #f5fbfe)', dark: colors.salt20 })};
  border: 1px solid ${cssThemedValue({ light: ' var(--color-bg-surface-brand, #ebf7fd)', dark: colors.salt16 })};
  border-radius: 10px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    flex-direction: column;
    padding: 18px;
    padding-block-end: 0;
  }
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  margin-block-end: 24px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    gap: 26px;
  }
`;

const CardItem = styled.div`
  display: flex;
  gap: 16px;
`;

const Icon = styled.div`
  svg {
    width: 20px;
    height: 20px;
    fill: ${cbSemanticCssVars.textPrimary};

    @media (max-width: ${MOBILE_BREAKPOINT}px) {
      width: 18px;
      height: 18px;
    }
  }
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Title = styled(Body1)`
  font-weight: 700;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    ${typographyCss.Body3()};
    font-weight: 700;
  }
`;

const Subtitle = styled(Body3)`
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    ${typographyCss.Small()};
  }
`;

const AppPreviewImage = styled.img`
  width: 145px;
  align-self: flex-end;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    align-self: center;
  }
`;

const WhatsNextCard = () => {
  const { formatMessage } = useIntl();

  const cardItems = [
    {
      icon: <Document />,
      title: formatMessage({ id: `${BASE_STEPS_PREVIEW_INTL_ID}.whats-next.tasks.approve-menu.title` }),
      subtitle: formatMessage({ id: `${BASE_STEPS_PREVIEW_INTL_ID}.whats-next.tasks.approve-menu.description` }),
    },
    {
      icon: <PhoneActionSettings />,
      title: formatMessage({ id: `${BASE_STEPS_PREVIEW_INTL_ID}.whats-next.tasks.get-tools.title` }),
      subtitle: formatMessage({ id: `${BASE_STEPS_PREVIEW_INTL_ID}.whats-next.tasks.get-tools.description` }),
    },
    {
      icon: <Sparkle />,
      title: formatMessage({ id: `${BASE_STEPS_PREVIEW_INTL_ID}.whats-next.tasks.go-live-date.title` }),
      subtitle: formatMessage({ id: `${BASE_STEPS_PREVIEW_INTL_ID}.whats-next.tasks.go-live-date.description` }),
    },
  ];

  return (
    <CardContainer>
      <CardContent>
        {cardItems.map((item, index) => (
          <CardItem key={index}>
            <Icon>{item.icon}</Icon>

            <TextContainer>
              <Title>{item.title}</Title>
              <Subtitle>{item.subtitle}</Subtitle>
            </TextContainer>
          </CardItem>
        ))}
      </CardContent>

      {<AppPreviewImage src={appPreview} alt="New orders screen" width={145} />}
    </CardContainer>
  );
};

export default WhatsNextCard;
